/**
 * The event → store patch reducer (spec M0-F5 §5.2, §5.3, acceptance group **P**).
 *
 * Pure, and deliberately kept away from the socket and from zustand: what makes a realtime
 * board hard is not the transport, it is the four rules below, and every one of them is a
 * function you can call from a test with three lines of setup.
 *
 *  1. **de-duplicate.** Delivery is at-least-once `[ADR-004 §3]` — the api relay and the
 *     worker's outbox consumer both publish — so the same envelope arrives twice and must
 *     change the store once (P2). A 2,000-entry seen-set of envelope ids does that.
 *  2. **drop stale patches.** Two publishers means no global order, so an envelope whose
 *     `entityUpdatedAt` is older than what the store already holds for that entity is
 *     dropped (P3). Not a sequence number: two api processes and the worker publish
 *     independently, so a gap-detecting sequence would produce false resyncs (§5.2).
 *  3. **never re-apply your own optimistic patch.** An envelope carrying a `mutationId` this
 *     client is still holding is *reconciliation*, not news (O1) — the store already painted
 *     it, and applying it again is the flicker the whole design exists to avoid.
 *  4. **compare ids as BigInt.** They are decimal strings of bigints starting at 10^12
 *     `[ADR-002]`; `'1000000000000' > '999999999999'` is `false` as text.
 *
 * `applyEvent` returns a **description** of what to do rather than doing it, so the store's
 * own transaction shape (one `set()` per batch, §5.2) stays the store's business.
 */
import {
  SEEN_EVENT_LIMIT,
  compareEventIds,
  isStalePatch,
  type EventEnvelope,
} from '@elizmonday/shared';

/** What the store should do with one envelope. */
export type StorePatch =
  | { kind: 'ignored'; reason: 'duplicate' | 'stale' | 'own-mutation' | 'unhandled' }
  | { kind: 'cells'; boardId: string; cells: CellPatch[] }
  | { kind: 'item.created'; boardId: string; groupId: string; item: Record<string, unknown> }
  | { kind: 'item.updated'; boardId: string; itemId: string; changed: Record<string, unknown> }
  | { kind: 'item.moved'; boardId: string; itemId: string; toGroupId: string | null; position: string | null; leftBoard: boolean }
  | { kind: 'item.removed'; boardId: string; itemIds: string[] }
  | { kind: 'group.created'; boardId: string; group: Record<string, unknown> }
  | { kind: 'group.updated'; boardId: string; groupId: string; changed: Record<string, unknown> }
  | { kind: 'group.removed'; boardId: string; groupId: string }
  | { kind: 'column.created'; boardId: string; column: Record<string, unknown> }
  | { kind: 'column.updated'; boardId: string; columnId: string; changed: Record<string, unknown> }
  | { kind: 'column.removed'; boardId: string; columnId: string }
  | { kind: 'board.updated'; boardId: string; changed: Record<string, unknown> }
  | { kind: 'board.removed'; boardId: string; state: string }
  | { kind: 'views.changed'; boardId: string }
  /** `incomplete`: the envelope did not carry the value it claims to change (P-277). */
  | { kind: 'refetch'; boardId: string; reason: 'truncated' | 'incomplete' };

export interface CellPatch {
  itemId: string;
  columnId: string;
  value: unknown;
  text?: string | null;
}

/** What the reducer needs to know about the store, without importing it. */
export interface PatchContext {
  /** The board this client is looking at; an envelope for another board is not ours. */
  boardId: string;
  /** The `updated_at` the store holds for an entity key, if any (rule 2). */
  updatedAtOf(key: string): string | null | undefined;
  /** Is this `mutationId` an optimistic patch this client is still holding (rule 3)? */
  isOwnPending(mutationId: string): boolean;
}

/**
 * The seen-set of rule 1. A plain `Set` with insertion-order eviction: `Map`/`Set` iteration
 * order in JS *is* insertion order, so the oldest key is the first one out and no timestamp
 * bookkeeping is needed.
 */
export class SeenEvents {
  private readonly ids = new Set<string>();

  constructor(private readonly limit: number = SEEN_EVENT_LIMIT) {}

  /** `true` the first time an id is offered, `false` every time after. */
  admit(id: string): boolean {
    if (this.ids.has(id)) return false;
    this.ids.add(id);
    if (this.ids.size > this.limit) {
      const oldest = this.ids.values().next().value;
      if (oldest !== undefined) this.ids.delete(oldest);
    }
    return true;
  }

  get size(): number {
    return this.ids.size;
  }
}

/** The entity key an envelope's `entityUpdatedAt` is about. */
export function entityKeyOf(event: EventEnvelope): string | null {
  const payload = event.payload as Record<string, unknown>;
  const id = (name: string) => (payload[name] === undefined ? null : String(payload[name]));
  switch (event.type) {
    case 'cell.changed': {
      const itemId = id('itemId');
      const columnId = id('columnId');
      return itemId && columnId ? `cell:${itemId}:${columnId}` : null;
    }
    case 'item.updated':
    case 'item.moved':
      return event.itemId ? `item:${event.itemId}` : null;
    case 'column.updated':
      return id('columnId') ? `column:${String(id('columnId'))}` : null;
    case 'group.updated':
      return id('groupId') ? `group:${String(id('groupId'))}` : null;
    case 'board.updated':
      return event.boardId ? `board:${event.boardId}` : null;
    default:
      return null;
  }
}

const ignored = (reason: 'duplicate' | 'stale' | 'own-mutation' | 'unhandled'): StorePatch => ({
  kind: 'ignored',
  reason,
});

/**
 * One envelope → one patch. `seen` is mutated (that is what a seen-set is for); everything
 * else is read-only.
 */
export function applyEvent(
  event: EventEnvelope,
  context: PatchContext,
  seen: SeenEvents,
): StorePatch {
  if (!seen.admit(event.id)) return ignored('duplicate');

  const key = entityKeyOf(event);
  if (key && isStalePatch(event.entityUpdatedAt, context.updatedAtOf(key))) return ignored('stale');

  // Our own optimistic write coming back: the store already has it (O1).
  if (event.mutationId && context.isOwnPending(event.mutationId)) return ignored('own-mutation');

  const payload = event.payload as Record<string, unknown>;
  const boardId = event.boardId ?? context.boardId;

  // A capped envelope carries ids and nothing else; the client refetches that entity (P6).
  if (payload.truncated === true) return { kind: 'refetch', boardId, reason: 'truncated' };

  switch (event.type) {
    case 'cell.changed': {
      /**
       * **P-277.** A missing `value` is not `value: null`.
       *
       * `payload.value ?? null` read *"this envelope does not carry the value"* as *"this cell
       * is now empty"* and painted the cell blank — over a value the server had just stored and
       * answered. Any envelope that has lost the key on the way (a relay that re-hydrates the
       * row, an older api, a payload trimmed in transit) could therefore empty a cell nobody
       * cleared, and only a reload put it back. A clear carries `value: null` **explicitly**, so
       * the key is present for every write that really did empty a cell; when it is absent the
       * only honest answer is "something changed here and I do not know what" — a refetch.
       */
      const carriesValue = (row: Record<string, unknown>) =>
        Object.prototype.hasOwnProperty.call(row, 'value');
      // A bulk edit is **one** envelope with a batch payload, not N messages (P5).
      if (payload.batch === true) {
        const columnId = String(payload.columnId ?? '');
        const rows = Array.isArray(payload.items) ? payload.items : [];
        if (!rows.every((entry) => carriesValue(entry as Record<string, unknown>))) {
          return { kind: 'refetch', boardId, reason: 'incomplete' };
        }
        return {
          kind: 'cells',
          boardId,
          cells: rows.map((entry) => {
            const row = entry as Record<string, unknown>;
            return {
              itemId: String(row.itemId),
              columnId: String(row.columnId ?? columnId),
              value: row.value ?? null,
              text: (row.text as string | null | undefined) ?? null,
            };
          }),
        };
      }
      if (payload.itemId === undefined || payload.columnId === undefined) return ignored('unhandled');
      if (!carriesValue(payload)) return { kind: 'refetch', boardId, reason: 'incomplete' };
      return {
        kind: 'cells',
        boardId,
        cells: [
          {
            itemId: String(payload.itemId),
            columnId: String(payload.columnId),
            value: payload.value ?? null,
            text: (payload.text as string | null | undefined) ?? null,
          },
        ],
      };
    }

    case 'item.created': {
      const item = (payload.item ?? payload) as Record<string, unknown>;
      const groupId = String(payload.groupId ?? item.groupId ?? '');
      if (!groupId) return ignored('unhandled');
      return { kind: 'item.created', boardId, groupId, item };
    }

    case 'item.updated': {
      const itemId = String(payload.itemId ?? event.itemId ?? '');
      if (!itemId) return ignored('unhandled');
      const changed = (payload.changed ?? payload) as Record<string, unknown>;
      return { kind: 'item.updated', boardId, itemId, changed };
    }

    case 'item.moved': {
      const itemId = String(payload.itemId ?? event.itemId ?? '');
      if (!itemId) return ignored('unhandled');
      // P7: a cross-board move is a *removal* here and a creation on the other board's room.
      const leftBoard =
        payload.toBoardId !== undefined && String(payload.toBoardId) !== String(boardId);
      if (leftBoard) return { kind: 'item.removed', boardId, itemIds: [itemId] };
      return {
        kind: 'item.moved',
        boardId,
        itemId,
        toGroupId: payload.toGroupId === undefined ? null : String(payload.toGroupId),
        position: payload.position === undefined ? null : String(payload.position),
        leftBoard: false,
      };
    }

    case 'item.deleted': {
      const ids = Array.isArray(payload.itemIds)
        ? payload.itemIds.map(String)
        : [String(payload.itemId ?? payload.id ?? event.itemId ?? '')].filter(Boolean);
      if (ids.length === 0) return ignored('unhandled');
      return { kind: 'item.removed', boardId, itemIds: ids };
    }

    case 'group.created': {
      const group = (payload.group ?? payload) as Record<string, unknown>;
      return { kind: 'group.created', boardId, group };
    }
    case 'group.updated':
    case 'group.moved': {
      const groupId = String(payload.groupId ?? payload.id ?? '');
      if (!groupId) return ignored('unhandled');
      const changed = (payload.changed ?? payload) as Record<string, unknown>;
      return { kind: 'group.updated', boardId, groupId, changed };
    }
    case 'group.deleted': {
      const groupId = String(payload.groupId ?? payload.id ?? '');
      if (!groupId) return ignored('unhandled');
      return { kind: 'group.removed', boardId, groupId };
    }

    case 'column.created': {
      const column = (payload.column ?? payload) as Record<string, unknown>;
      return { kind: 'column.created', boardId, column };
    }
    case 'column.updated':
    case 'column.moved': {
      const columnId = String(payload.columnId ?? payload.id ?? '');
      if (!columnId) return ignored('unhandled');
      const changed = (payload.changed ?? payload) as Record<string, unknown>;
      return { kind: 'column.updated', boardId, columnId, changed };
    }
    case 'column.deleted': {
      const columnId = String(payload.columnId ?? payload.id ?? '');
      if (!columnId) return ignored('unhandled');
      return { kind: 'column.removed', boardId, columnId };
    }

    case 'board.updated': {
      const changed = (payload.changed ?? payload) as Record<string, unknown>;
      return { kind: 'board.updated', boardId, changed };
    }
    case 'board.archived':
    case 'board.deleted':
      return { kind: 'board.removed', boardId, state: String(payload.state ?? 'deleted') };
    case 'board.restored':
      return { kind: 'board.updated', boardId, changed: { state: 'active' } };

    case 'view.created':
    case 'view.updated':
    case 'view.deleted':
      // The tabs bar refetches: a view carries settings this client may be editing, and
      // merging two people's view settings is not a thing F5 promises (§5.2).
      return { kind: 'views.changed', boardId };

    default:
      return ignored('unhandled');
  }
}

/** Newest-first, for the rare caller that has to order a bag of envelopes. */
export function sortEnvelopes(events: readonly EventEnvelope[]): EventEnvelope[] {
  return [...events].sort((a, b) => compareEventIds(a.id, b.id));
}
